import { useCallback, useMemo } from "react";
import styled from "styled-components";

import { AdjustableDigit } from "./AdjustableDigit";

interface Digit {
  maxValue: number;
  adjustRemaining?: (remaining: number) => number; //If you need to clip the whole value to a set number
}

interface StaticText {
  text: string;
}

interface DynamicText {
  getText: (value: number, maxValue: number) => string;
  adjustBy: (maxValue: number) => number;
}

export type AdjustableNumberColumn = { isHidden?: boolean } & (Digit | StaticText | DynamicText);

function columnIsDynamicText(column: AdjustableNumberColumn): column is DynamicText {
  return (column as DynamicText).getText !== undefined;
}

function columnIsStaticText(column: AdjustableNumberColumn): column is StaticText {
  return (column as StaticText).text !== undefined;
}

interface ValuedDigit extends Digit {
  value: number;
  adjustBy: number;
}

type ValuedColumn = { isHidden: boolean } & (ValuedDigit | StaticText | DynamicText);

type DivProps = React.HTMLAttributes<HTMLDivElement>;

interface Props {
  value: number;
  setValue: (newVal: number) => void;
  columns: Array<AdjustableNumberColumn>;
  onValueIsNegative?: (value: number, maxValue: number) => number;
  onValueIsOver?: (value: number, maxValue: number) => number;
  showButtons?: boolean;
  minValue?: number;
  textColor?: string;
}

export const AdjustableNumber: React.FC<Props & DivProps> = ({
  value,
  setValue,
  columns,
  onValueIsNegative,
  onValueIsOver,
  showButtons = true,
  minValue = 0,
  textColor = "var(--secondary)",
  ...props
}) => {
  const [valuedColumns, , maxValue] = useMemo(
    () =>
      columns.reduceRight<[Array<ValuedColumn>, number, number]>(
        ([prevArray, remaining, adjustBy], column) => {
          if (columnIsStaticText(column) || columnIsDynamicText(column)) {
            return [
              [{ ...column, isHidden: Boolean(column.isHidden) }, ...prevArray],
              remaining,
              adjustBy,
            ];
          }
          if (column.adjustRemaining) {
            remaining = column.adjustRemaining(remaining);
          }
          return [
            [
              {
                ...column,
                value: Math.floor(remaining % column.maxValue),
                adjustBy,
                isHidden: Boolean(column.isHidden),
              },
              ...prevArray,
            ],
            Math.floor(remaining / column.maxValue),
            adjustBy * column.maxValue,
          ];
        },
        [[], Math.ceil(value), 1],
      ),
    [columns, value],
  );

  const incrementBy = useCallback(
    (amount: number): void => {
      let newVal = value + amount;
      if (newVal > maxValue && onValueIsOver) {
        newVal = onValueIsOver(newVal, maxValue);
      }
      setValue(newVal);
    },
    [value, maxValue, onValueIsOver, setValue],
  );

  function decrementBy(amount: number): void {
    let newVal = value - amount;
    if (newVal < minValue && onValueIsNegative) {
      newVal = onValueIsNegative(newVal, maxValue);
    }
    setValue(newVal);
  }

  return (
    <Container {...props}>
      {valuedColumns.map((col, i) => {
        if (!col.isHidden) {
          return columnIsStaticText(col) ? (
            <TimerText textColor={textColor} key={i}>
              {col.text}
            </TimerText>
          ) : columnIsDynamicText(col) ? (
            <AdjustableDigit
              key={i}
              value={col.getText(value, maxValue)}
              onDecrement={decrementBy.bind(null, col.adjustBy(maxValue))}
              onIncrement={incrementBy.bind(null, col.adjustBy(maxValue))}
              showButtons={showButtons}
              variant="secondary"
            />
          ) : (
            <AdjustableDigit
              key={i}
              value={col.value}
              onDecrement={decrementBy.bind(null, col.adjustBy)}
              onIncrement={() => incrementBy(col.adjustBy)}
              showButtons={showButtons}
              textColor={textColor}
            />
          );
        } else {
          return null;
        }
      })}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 0.1em;
  width: 100%;
  aspect-ratio: 2;
  container-type: size;
  container-name: adjustable-number;
`;

const TimerText = styled.p<{ textColor: string }>`
  font-size: 16cqw;
  line-height: 16cqw;
  color: ${(props) => props.textColor};
`;
